/**
 * First open = already working (owner, 2026-10-13: "Imagine it's got everything there right when they open it. It's an easier sell
 * when it knows you already and already working to fix problems").
 *
 * MEASURED path of a claimed prep's owner (2026-10-13, before this change): /see/<token> "Make it yours" → claim →
 * window.location.assign("/walk/review?org=…") → "Review what Zak set up" (room list, "Start with the kitchen") → … → Today, where
 * the walk card sat at the bottom and the inspection plan was one quiet row. No setup questions on that path (the org exists, so
 * App.tsx never opens Setup), but the first screen was a review chore, not what we already know.
 *
 * Now the claim opens Today, and Today's first card (managers; the web's ForYou and the phone's Today read this one view) leads with
 * what we know and what is already moving:
 *   lead   "<Place>: Zak walked your kitchen, dining room and restroom (3 rooms, 24 things listed)."
 *   cited  "Flies were cited. Your 5-step plan is ready."     (or the clean line, or "Your last inspection cited 2 items. …")
 *   step   "Step 1: Find where flies gather: trash, drains, mop sink, produce, back door."
 *   primary the top quick win's ONE button ("Let's fix that" → its plan at step 1, /inspection#plan-flies; owner, 2026-10-14);
 *          a clean inspection: "Start today's checks".
 *   wins   "Your quick wins": the top 3 (quickwins.ts) with "1 of 5 fixed".
 *   then   "Then today's checks, below." — the day's own cards follow it.
 *   review "Review what Zak set up" (the walk review), a secondary link.
 * Shown while the walk review card would be (a prep handed off, not all rooms checked, no "Not now"): the same lifetime, no new
 * storage, and it replaces that card (never both). Food at risk still comes first on the page (feed.ts F1). Pure.
 */
import type { RoomWalk } from "./roomwalk.ts";
import { setUpBy, type ReviewCard } from "./walkreview.ts";
import { CLEAN_LINE, LAST_INSPECTION_TITLE, type LastInspectionView } from "./lastinspection.ts";
import { anchorOf } from "./citedplan.ts";
import { LETS_FIX } from "./quickwins.ts";

/**
 * A finished room for the reveal (reveal.ts): its name, how many things were listed in it, and its photos (the first few scan ids,
 * newest walk's order; the screen fetches each through the walk's own photo route, which only a member can open).
 */
export interface WalkedRoom { key: string; name: string; things: number; photos: string[]; photoCount: number }
/** What the walk found, for the first card: the walked rooms in the walk's order, and how many things were listed in them. */
export interface WalkedSummary { setBy: string; rooms: string[]; things: number; /** The walk's id, for its photos. Absent on older servers. */ walkId?: string; /** Each finished room, for the reveal. Absent on older servers. */ roomCards?: WalkedRoom[] }

export const REVEAL_PHOTOS_PER_ROOM = 2;
export function walkedSummary(walk: Pick<RoomWalk, "rooms" | "prep" | "startedByName"> & { id?: string } | null | undefined): WalkedSummary | null {
  if (!walk) return null;
  const walked = walk.rooms.filter((r) => r.acknowledged);
  return {
    setBy: setUpBy(walk), rooms: walked.map((r) => r.name), things: walked.reduce((n, r) => n + (r.acknowledged?.items.length ?? 0), 0),
    ...(walk.id ? { walkId: walk.id } : {}),
    roomCards: walked.map((r) => ({ key: r.key, name: r.name, things: r.acknowledged?.items.length ?? 0, photos: r.scanIds.slice(0, REVEAL_PHOTOS_PER_ROOM), photoCount: r.scanIds.length })),
  };
}

export interface FirstOpenView {
  lead: string;
  cited: string | null;
  step: string | null;
  primary: { label: string; to: string };
  then: string;
  review: { label: string; to: string } | null;
  /** "Your quick wins" (quickwins.ts): the top 3 from the real record, each with its one button, and "1 of 5 fixed". Null when nothing was cited. */
  wins: { title: string; line: string; items: { id: string; title: string; line: string; label: string; to: string }[] } | null;
}

export const FIRST_OPEN_THEN = "Then today's checks, below.";
export const FIRST_OPEN_CHECKS = { label: "Start today's checks", to: "/do" } as const;
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const andList = (xs: readonly string[]) => (xs.length <= 1 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`);
const roomWord = (name: string) => (/^[A-Z][a-z]/.test(name) ? name.charAt(0).toLowerCase() + name.slice(1) : name);

/** "Loki's: Zak walked your kitchen, dining room and restroom (3 rooms, 24 things listed)." At most 3 room names, then "and 2 more". */
export function firstOpenLead(place: string, w: WalkedSummary | null): string {
  if (!w || !w.rooms.length) return `${place} is set up${w ? ` by ${w.setBy}` : ""}.`;
  const names = w.rooms.slice(0, 3).map(roomWord);
  const more = w.rooms.length - names.length;
  return `${place}: ${w.setBy} walked your ${andList(more ? [...names, `${more} more`] : names)} (${plural(w.rooms.length, "room")}, ${plural(w.things, "thing")} listed).`;
}

/**
 * The first card, or null when it isn't a first open (no review card: not a handed-off prep, reviewed, or "Not now").
 * `inspection` is the last inspection's view when it is shown (lastinspection.ts), else null.
 */
export function firstOpenView(o: { place: string; walked: WalkedSummary | null; review: ReviewCard | null; inspection: LastInspectionView | null }): FirstOpenView | null {
  if (!o.review) return null;
  const review = { label: o.review.title, to: o.review.to };
  const base = { lead: firstOpenLead(o.place, o.walked), then: FIRST_OPEN_THEN, review, wins: null };
  const v = o.inspection;
  if (!v) return { ...base, cited: null, step: null, primary: { ...FIRST_OPEN_CHECKS } };
  if (v.clean) return { ...base, cited: CLEAN_LINE, step: null, primary: { ...FIRST_OPEN_CHECKS } };
  // "Let's fix that" from the real record (owner, 2026-10-14): the quick wins lead, top 3, each with its one button.
  const wins = v.wins && v.wins.top.length ? {
    title: v.wins.title, line: v.wins.line,
    items: v.wins.top.map((w) => ({ id: w.id, title: w.title, line: w.line, label: w.action.label, to: w.action.to })),
  } : null;
  // A check-back that is due comes first: it is the one question the plan is waiting on.
  const due = v.items.map((i) => i.plan).find((p) => p?.recheck.ask);
  if (due) return { ...base, wins, cited: due.recheck.ask!.question, step: null, primary: { label: "Check back now", to: `/inspection#${anchorOf(due)}` } };
  // The one button opens a plan at its next step ("Let's fix that"): the first plan among the quick wins, else the first cited
  // item whose fixes for today aren't done (critical first). The wins list beside it carries the printouts and checks.
  const top = v.wins?.top[0] ?? null;
  const planWin = v.wins?.top.find((w) => w.kind === "plan");
  const plan = (planWin ? v.items.find((i) => i.code === planWin.code)?.plan : v.items.find((i) => i.plan && i.plan.now.done < i.plan.now.steps.length)?.plan) ?? null;
  if (plan && !plan.recheck.closed) {
    const to = `/inspection#${anchorOf(plan)}`;
    const cited = plan.today ? `${plan.today.line}.` : `${plan.title}.`;
    // The plan's first part first (a repeat citation starts with the way in), then its first step not done yet.
    for (const part of plan.order) {
      if (part !== "now" && part !== "source") continue;
      const steps = plan[part].steps;
      const i = steps.findIndex((s) => !s.done);
      if (i < 0) continue;
      return { ...base, wins, cited, step: `Step ${i + 1}: ${steps[i]!.say}`, primary: { label: LETS_FIX, to } };
    }
    return { ...base, wins, cited, step: null, primary: { label: "Open the plan", to } };
  }
  const n = v.items.length;
  if (top) return { ...base, wins, cited: `Your last inspection cited ${plural(n, "item")}. ${v.wins!.line}.`, step: top.title, primary: { label: top.action.label, to: top.action.to } };
  return { ...base, wins, cited: `Your last inspection cited ${plural(n, "item")}. Each one has its next step.`, step: null, primary: { label: `See ${LAST_INSPECTION_TITLE.toLowerCase()}`, to: "/inspection" } };
}

/** Every fixed string the first card writes (the copy tests read it). */
export const FIRST_OPEN_WORDS: readonly string[] = [FIRST_OPEN_THEN, FIRST_OPEN_CHECKS.label, LETS_FIX, "Open the plan", "Check back now", "Each one has its next step.", `See ${LAST_INSPECTION_TITLE.toLowerCase()}`];
